import * as React from 'react';
import getCalculatedTextAreaHeight from './CalculateResizeHeight';
import { debounce } from './Debounce';
import StyledTextArea, {
  StyledTextareaProps,
  IAutosizeOptions
} from './TextArea.style';
import { useDevWarning } from '../../../utils/hooks';

// Components
import Typography from '../../commonComponents/Typography/Typography';

export interface ITextAreaProps extends StyledTextareaProps {}
/**
 * Handles setting up all resize logic needed for autoresize feature
 */
const AutoSizeTextArea: React.FC<ITextAreaProps> = ({
  onChange,
  autosize,
  ...props
}) => {
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
  const textAreaClone = React.useRef<HTMLTextAreaElement>(null);
  const [resizeStyles, setResizeStyles] = React.useState({});
  const handleTextareaResize = React.useCallback(() => {
    /**
     * These refs will never be null or undefined, react makes sure of this.
     * these checks are satisfying typescript
     */
    if (textAreaRef.current !== null && textAreaClone.current !== null) {
      // calculate new node height
      const { minRows, maxRows } = autosize as IAutosizeOptions;
      const newStyles = getCalculatedTextAreaHeight(
        textAreaRef.current,
        textAreaClone.current,
        minRows,
        maxRows
      );
      setResizeStyles({ ...newStyles });
    }
  }, [autosize]);
  /**
   * Setting up resize event listener
   * We need this listener to make sure we update the height of Textarea
   * if screen size changes
   */
  React.useEffect(() => {
    const [debouncedResize, clear] = debounce(handleTextareaResize);
    window.addEventListener('resize', debouncedResize);
    return () => {
      // clean up any remaining timeouts on unmount
      clear();
      window.removeEventListener('resize', debouncedResize);
    };
  }, [handleTextareaResize]);
  // we need these changes to take hold synchronously before the dom paints
  React.useLayoutEffect(() => {
    handleTextareaResize();
  }, [handleTextareaResize]);

  function handleOnChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
    handleTextareaResize();
    // call users onChange
    if (onChange) {
      onChange(e);
    }
  }
  return (
    <>
      <StyledTextArea
        onChange={handleOnChange}
        autosize={autosize}
        ref={textAreaRef}
        style={{ ...resizeStyles }}
        {...props}
      />
      <textarea aria-hidden ref={textAreaClone} />
    </>
  );
};

const TextArea: React.FC<ITextAreaProps> = ({
  autosize,
  maximum,
  ...props
}) => {
  useDevWarning(
    'Please use a `maximum` value greater than 0.',
    () => maximum !== undefined && maximum !== null && maximum < 1
  );

  const baseTextAreaProps = {
    p: 2,
    fontSize: 0,
    lineHeight: 1,
    width: '100%',
    ...props
  };
  return autosize ? (
    <>
      <AutoSizeTextArea autosize={autosize} {...baseTextAreaProps} />
      {maximum !== undefined && maximum !== null && (
        <Typography
          kind={props.hasError ? 'feedback' : 'hint'}
          pl="8px"
          textAlign="right"
          width="100%"
          marginTop="5px"
        >
          {props.value ? props.value.toString().length : 0}/{maximum}
        </Typography>
      )}
    </>
  ) : (
    <>
      <StyledTextArea {...baseTextAreaProps} />
      {maximum !== undefined && maximum !== null && (
        <Typography
          kind={props.hasError ? 'feedback' : 'hint'}
          textAlign="right"
          width="100%"
          marginTop="8px"
        >
          {props.value ? props.value.toString().length : 0}/{maximum}
        </Typography>
      )}
    </>
  );
};

TextArea.defaultProps = {
  autosize: false
};

export default TextArea;
